import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { gsap } from 'gsap';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'iOS & macOS', path: '/apple-dev' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        '.mobile-nav-link',
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, stagger: 0.1, duration: 0.5, ease: 'power3.out' }
      );
    }
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass py-4' : 'py-6 bg-transparent'
        }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="logo"
          className="relative z-50 flex items-center gap-2"
        >
          {/* Short logo for mobile */}
          <img
            src="/SQRX_Icon.png"
            alt="SQRX"
            className="h-10 w-10 sm:hidden object-contain"
          />
          {/* Full logo for desktop */}
          <img
            src="/SQRX_White_Logo.png"
            alt="Squarex Studios"
            className="h-10 hidden sm:block object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-sm font-medium transition-colors animated-underline ${location.pathname === link.path
                ? 'text-[#af0000]'
                : 'text-[#94a3b8] hover:text-white'
                }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/shotlab"
            data-testid="shotlab-nav-btn"
            className="shotlab-btn"
          >
            <span className="shotlab-btn-inner">SQRX ShotLab</span>
          </Link>
          <Link
            to="/contact"
            data-testid="start-project-btn"
            className="flex items-center gap-2 bg-[#af0000] hover:bg-[#8a0000] text-white px-6 py-3 rounded-full font-semibold text-sm transition-all hover:scale-105"
          >
            Start a Project
            <ArrowUpRight size={16} />
          </Link>
        </div>

        {/* Mobile: ShotLab Button + Menu Toggle */}
        <div className="lg:hidden flex items-center gap-3 relative z-50">
          <Link
            to="/shotlab"
            data-testid="shotlab-mobile-btn"
            className="shotlab-btn"
            onClick={() => setIsOpen(false)}
          >
            <span className="shotlab-btn-inner text-xs px-3 py-2">SQRX ShotLab</span>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`mobile-menu ${isOpen ? 'open' : ''}`}
      >
        {/* Close button at top right - larger and more visible */}


        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              className={`mobile-nav-link text-3xl font-bold font-['Syne'] transition-colors ${location.pathname === link.path
                ? 'text-[#af0000]'
                : 'text-white hover:text-[#af0000]'
                }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {link.name}
            </Link>
          ))}

          {/* ShotLab button inside mobile menu - same size as Start a Project */}
          <Link
            to="/shotlab"
            className="mobile-nav-link shotlab-btn mt-4"
          >
            <span className="shotlab-btn-inner px-8 py-4 text-base">SQRX ShotLab</span>
          </Link>

          <Link
            to="/contact"
            className="mobile-nav-link flex items-center gap-2 bg-[#af0000] hover:bg-[#8a0000] text-white px-8 py-4 rounded-full font-semibold transition-all"
          >
            Start a Project
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
